import { useEffect } from "react";
import { Link } from "react-router-dom";
import ContactForm from "./components/ContactForm";
import { setPageMeta } from "@/utils/seo";

const values = [
  {
    icon: "ri-database-2-line",
    title: "海量内容聚合",
    desc: "覆盖最新电影、热播电视剧与经典影视作品，每日整理更新，内容分类清晰。",
  },
  {
    icon: "ri-compasses-2-line",
    title: "便捷发现体验",
    desc: "通过分类筛选、专题合集与相关推荐，帮助你在海量内容中快速锁定目标。",
  },
  {
    icon: "ri-shield-check-line",
    title: "专注观影引导",
    desc: "以作品信息与资源索引为核心，提供真实、清晰的影视资料与观看引导。",
  },
];

const contactItems = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "contact@91n.example" },
  { icon: "ri-map-pin-line", label: "内容中心", value: "线上运营 · 全国用户服务" },
  { icon: "ri-time-line", label: "更新时间", value: "全年无休 · 每日更新" },
];

export default function AboutPage() {
  useEffect(() => {
    setPageMeta(
      "关于我们 - 91n影视大全",
      "了解91n影视大全的定位、内容范围与服务理念，获取联系方式与版权、免责声明说明。"
    );
  }, []);

  return (
    <div className="w-full">
      <section className="relative w-full h-[220px] md:h-[280px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20warm%20modern%20home%20theater%20with%20a%20large%20screen%20glowing%20and%20cozy%20seating%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20lighting%20with%20deep%20shadows%2C%20high%20detail%2C%20wide%20composition&width=1600&height=500&seq=about-banner&orientation=landscape"
          alt="关于91n影视大全"
          title="关于我们 - 91n影视大全"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/15"></div>
        <div className="relative z-10 w-full h-full px-4 md:px-8 flex flex-col justify-center">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-white/70 mb-3">
            <Link to="/" className="hover:text-white transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">关于我们</span>
          </nav>
          <h1 className="font-heading font-black text-white text-2xl md:text-4xl mb-2">关于 91n 影视大全</h1>
          <p className="text-white/80 text-sm md:text-base max-w-2xl">
            一个为影视爱好者打造的综合影视资源门户，专注发现、浏览与推荐。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-10 md:py-12">
        <section className="mb-12 max-w-4xl">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
            <a href="#about-intro" id="about-intro" className="hover:text-primary-500 transition-colors">
              网站简介
            </a>
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
            91n 影视大全是一个聚合电影与电视剧资源的综合影视门户。我们围绕“发现好内容”这一核心，
            持续整理最新上映影片、热播剧集以及经久不衰的经典影视作品，并通过类型、地区、年份、评分等多维筛选，
            以及专题合集与相关推荐，帮助用户更高效地找到真正想看的作品。
          </p>
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
            我们的目标用户是注重内容多样性、追求最新影视资讯、习惯在线观看的普通观众与影视爱好者。
            无论是想追一部新剧、重温一部经典，还是单纯想找点下饭的内容，91n 都希望成为你可靠的影视发现指南。
          </p>
        </section>

        <section className="mb-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
            <a href="#about-values" id="about-values" className="hover:text-primary-500 transition-colors">
              服务理念
            </a>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {values.map((v) => (
              <div key={v.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
                <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-500 flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="font-semibold text-foreground-950 text-base mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
            <a href="#about-contact" id="about-contact" className="hover:text-primary-500 transition-colors">
              联系我们
            </a>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              {contactItems.map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-4 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200"
                >
                  <span className="shrink-0 w-11 h-11 rounded-lg bg-accent-100 text-accent-600 flex items-center justify-center">
                    <i className={`${c.icon} text-xl`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm md:text-base font-medium text-foreground-900 truncate">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <ContactForm />
          </div>
        </section>

        <section className="max-w-4xl">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
            <a href="#about-disclaimer" id="about-disclaimer" className="hover:text-primary-500 transition-colors">
              版权与免责声明
            </a>
          </h2>
          <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6 space-y-3 text-sm text-foreground-600 leading-relaxed">
            <p>
              本站为影视信息演示站点，页面中出现的所有影视名称、封面、简介及相关资料均为演示用途的虚构内容，
              仅用于展示产品结构与交互效果，不代表任何真实影视作品。
            </p>
            <p>
              本站不提供真实的视频播放、下载或任何形式的影视资源分发服务，页面中的播放、下载入口仅为交互演示，
              点击后将提示演示状态，请勿将其理解为可用的播放能力。
            </p>
            <p>
              如本站内容无意中侵犯了任何第三方的合法权益，请通过上方联系方式与我们取得联系，我们将及时处理。
              本站内容仅供学习交流使用，不得用于任何商业用途。
            </p>
            <p className="text-foreground-500">© {new Date().getFullYear()} 91n影视大全 · 保留所有权利。</p>
          </div>
        </section>
      </div>
    </div>
  );
}